import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  CalendarClock,
  ClipboardCheck,
  Hourglass,
  AlertTriangle,
  CheckCircle2,
  Users,
  Globe,
  User as UserIcon,
  Wallet,
} from 'lucide-react';
import { apiDashboard, apiReports, apiBudget } from '../api/client.js';
import { useFilterStore, toFilterQuery } from '../hooks/useFilterStore.js';
import DashboardWidget from '../components/DashboardWidget.jsx';
import BudgetAlertsBanner from '../components/BudgetAlertsBanner.jsx';
import AffaireTimeSection from '../components/AffaireTimeSection.jsx';
import TimeStatementSection from '../components/TimeStatementSection.jsx';
import LineAreaChart from '../components/charts/LineAreaChart.jsx';
import BarChart from '../components/charts/BarChart.jsx';
import Donut from '../components/charts/Donut.jsx';

/**
 * Sprint 13 — Tableau de bord (Accueil).
 *
 * - Partie personnelle (tous rôles) : 3 indicateurs sur la propre Person.
 * - Partie équipe (MANAGER / ADMIN) : mêmes indicateurs agrégés + tableau des
 *   personnes avec taux de remplissage.
 * - ADMIN : toggle « Mon équipe » / « Tout le monde » sur la partie agrégée.
 *
 * Capacité : 8h/jour ouvré moins fériés + congés validés (calcul serveur).
 * Seuils d'alerte + nombre de semaines de scheduling : paramétrables en admin
 * (onglet Paramètres).
 */

// ---------- Helpers d'affichage ----------

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

function fmtPct(p) {
  return p === null || p === undefined ? '—' : `${p}%`;
}

/**
 * #17 — Affiche une semaine du **lundi au vendredi** (on planifie/log sur 5
 * jours) au lieu de lundi→dimanche. `weekStart` est le lundi (ISO).
 */
function fmtWeekMonFri(weekStartIso) {
  if (!weekStartIso) return '';
  const [y, m, d] = weekStartIso.split('-').map(Number);
  const friday = new Date(Date.UTC(y, m - 1, d + 4));
  return `Du ${fmtDate(weekStartIso)} au ${fmtDate(friday.toISOString().slice(0, 10))}`;
}

function fmtHours(h) {
  if (h === null || h === undefined) return '0';
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

/**
 * Couleur d'un taux : sous-saisie (alert) → danger, sur-saisie/heures sup
 * (overAlert, ticket #15) → warning orange, sinon succès ; null → neutre.
 */
function pctColor(metric) {
  if (!metric || metric.fillPct === null) return 'var(--text-muted)';
  if (metric.alert) return 'var(--danger)';
  if (metric.overAlert) return 'var(--warning)';
  return 'var(--success)';
}

// ---------- Sous-composants ----------

function FillBar({ pct, color }) {
  const width = pct === null || pct === undefined ? 0 : Math.min(100, pct);
  return (
    <div
      style={{
        height: 8,
        borderRadius: 999,
        background: 'var(--glass)',
        overflow: 'hidden',
        border: '1px solid var(--glass-border)',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${width}%`,
          background: color,
          borderRadius: 999,
          transition: 'width 200ms',
        }}
      />
    </div>
  );
}

/**
 * Ticket #49 — une carte est un raccourci : cliquer ouvre l'écran de détail
 * qui ne montre que les jours concernés par l'indicateur. Rendue en `button`
 * (et non en `div` avec `onClick`) pour rester atteignable au clavier.
 */
function MetricCard({ Icon, title, subtitle, metric, footer, onOpen }) {
  const color = pctColor(metric);
  const alert = metric?.alert;
  const overAlert = metric?.overAlert;
  const borderColor = alert ? 'var(--danger)' : overAlert ? 'var(--warning)' : 'var(--glass-border)';
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      title={onOpen ? `Voir le détail — ${title}` : undefined}
      style={{
        font: 'inherit',
        textAlign: 'left',
        cursor: onOpen ? 'pointer' : 'default',
        background: 'var(--card-bg)',
        border: `1px solid ${borderColor}`,
        borderRadius: 'var(--radius-md)',
        padding: '16px 18px',
        boxShadow: 'var(--shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
        {alert ? (
          <AlertTriangle size={15} style={{ color: 'var(--danger)', marginLeft: 'auto' }} />
        ) : overAlert ? (
          <AlertTriangle size={15} style={{ color: 'var(--warning)', marginLeft: 'auto' }} title="Heures supplémentaires (>100%)" />
        ) : metric && metric.fillPct !== null ? (
          <CheckCircle2 size={15} style={{ color: 'var(--success)', marginLeft: 'auto' }} />
        ) : null}
      </div>
      {subtitle ? (
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{subtitle}</div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>
          {fmtPct(metric?.fillPct)}
        </span>
      </div>
      <FillBar pct={metric?.fillPct} color={color} />
      {footer ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{footer}</div>
      ) : null}
    </button>
  );
}

/** Rangée de 3 cartes (scheduling, sem. préc, sem. en cours). */
function MetricsRow({ metrics, window, config, onOpenDetail }) {
  if (!metrics) return null;
  const { scheduling, schedulingMonth, prevWeek, currentWeek } = metrics;
  const schedFooter = (m) => (
    <>
      {fmtHours(m.allocatedHours)}h planifiées / {fmtHours(m.capacityHours)}h
      {m.unplannedWorkingDays > 0 ? (
        <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
          {' '}
          · {m.unplannedWorkingDays} j non planifié{m.unplannedWorkingDays > 1 ? 's' : ''}
        </span>
      ) : (
        <span style={{ color: 'var(--success)' }}> · tout planifié</span>
      )}
      {/* Ticket #33 — les congés en attente sont retirés de la capacité à
          planifier ; on l'indique explicitement au lieu de dégrader le taux. */}
      {m.pendingTimeOffDays > 0 ? (
        <div style={{ color: 'var(--warning)', marginTop: 2 }}>
          {fmtHours(m.pendingTimeOffDays)} j de congé en attente de validation
        </div>
      ) : null}
    </>
  );
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
      }}
    >
      {/* #5 — deux visions de la planification : mois calendaire complet + reste du mois. */}
      {schedulingMonth ? (
        <MetricCard
          Icon={CalendarClock}
          title="Planification (mois complet)"
          subtitle={
            window
              ? `Du ${fmtDate(window.schedulingMonthFrom)} au ${fmtDate(window.schedulingMonthTo)}`
              : null
          }
          metric={schedulingMonth}
          footer={schedFooter(schedulingMonth)}
          onOpen={onOpenDetail ? () => onOpenDetail('schedulingMonth') : undefined}
        />
      ) : null}
      <MetricCard
        Icon={CalendarClock}
        title="Planification (reste du mois)"
        subtitle={
          window
            ? `Du ${fmtDate(window.schedulingFrom)} au ${fmtDate(window.schedulingTo)}`
            : null
        }
        metric={scheduling}
        footer={schedFooter(scheduling)}
        onOpen={onOpenDetail ? () => onOpenDetail('scheduling') : undefined}
      />
      <MetricCard
        Icon={ClipboardCheck}
        title="Logtime — sem. précédente"
        subtitle={window ? fmtWeekMonFri(window.prevWeekStart) : null}
        metric={prevWeek}
        onOpen={onOpenDetail ? () => onOpenDetail('prevWeek') : undefined}
        footer={`${fmtHours(prevWeek.loggedHours)}h saisies / ${fmtHours(prevWeek.capacityHours)}h attendues`}
      />
      <MetricCard
        Icon={Hourglass}
        title="Logtime — sem. en cours"
        subtitle={window ? fmtWeekMonFri(window.currentWeekStart) : null}
        metric={currentWeek}
        onOpen={onOpenDetail ? () => onOpenDetail('currentWeek') : undefined}
        footer={
          <>
            {fmtHours(currentWeek.loggedHours)}h saisies /{' '}
            {fmtHours(currentWeek.capacityHours)}h attendues
            {/* Ticket #33 — la référence reste la semaine entière (#15), mais on
                affiche l'attendu à date : saisir des jours futurs n'a pas de
                sens, et l'alerte se base désormais sur cet à-date. */}
            {currentWeek.toDateCapacityHours < currentWeek.capacityHours ? (
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                À date : {fmtHours(currentWeek.toDateLoggedHours)}h /{' '}
                {fmtHours(currentWeek.toDateCapacityHours)}h
                {currentWeek.toDateFillPct != null ? ` · ${currentWeek.toDateFillPct}%` : ''}
              </div>
            ) : null}
          </>
        }
      />
    </div>
  );
}

/** Cellule de taux dans le tableau d'équipe. */
function PctCell({ metric }) {
  const color = pctColor(metric);
  return (
    <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
      <span style={{ color, fontWeight: 600 }}>{fmtPct(metric?.fillPct)}</span>
      {metric?.alert ? (
        <AlertTriangle
          size={13}
          style={{ color: 'var(--danger)', marginLeft: 6, verticalAlign: '-2px' }}
        />
      ) : metric?.overAlert ? (
        <AlertTriangle
          size={13}
          title="Heures supplémentaires (>100%)"
          style={{ color: 'var(--warning)', marginLeft: 6, verticalAlign: '-2px' }}
        />
      ) : null}
    </td>
  );
}

function TeamTable({ rows }) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.lastName.localeCompare(b.lastName)),
    [rows],
  );
  const th = {
    padding: '8px 12px',
    textAlign: 'right',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--text-muted)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        overflow: 'auto',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
            <th style={{ ...th, textAlign: 'left' }}>Personne</th>
            <th style={th}>Planification</th>
            <th style={th}>Sem. préc.</th>
            <th style={th}>Sem. en cours</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.personId}
              style={{ borderBottom: '1px solid var(--glass-border)' }}
            >
              <td style={{ padding: '8px 12px' }}>
                <div style={{ color: 'var(--text)', fontWeight: 500 }}>
                  {r.firstName} {r.lastName}
                </div>
                {r.jobTitle ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{r.jobTitle}</div>
                ) : null}
              </td>
              <PctCell metric={r.scheduling} />
              <PctCell metric={r.prevWeek} />
              <PctCell metric={r.currentWeek} />
            </tr>
          ))}
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>
                Aucune personne dans ce périmètre.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

const sectionTitle = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

// ---------- Vue principale ----------

// ---------- Section Utilisation (widgets multi-charts) ----------

function startOfUtcDay(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function addDaysUtc(d, n) {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
}
function isoOfUtc(d) {
  return d.toISOString().slice(0, 10);
}
function mondayOfUtc(d) {
  const wd = ((d.getUTCDay() + 6) % 7) + 1;
  return addDaysUtc(startOfUtcDay(d), -(wd - 1));
}
function fmtBucketLabel(iso) {
  if (!iso) return '';
  const [, m, day] = iso.split('-');
  return `${day}/${m}`;
}

function UtilizationSection({ scope, onDrillDown }) {
  const { conditions } = useFilterStore('schedule');
  const filterQuery = useMemo(() => toFilterQuery(conditions), [conditions]);

  // Fenêtre par défaut : 12 semaines à partir du lundi courant, granularité semaine.
  const window = useMemo(() => {
    const from = mondayOfUtc(new Date());
    return { from: isoOfUtc(from), to: isoOfUtc(addDaysUtc(from, 83)) };
  }, []);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiReports
      .utilization({
        from: window.from,
        to: window.to,
        period: 'week',
        viewMode: 'scheduled',
        scope,
        filter: filterQuery,
      })
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [window.from, window.to, scope, filterQuery]);

  const series = report?.series ?? [];
  const totals = report?.totals;

  const utilizationDonut = totals
    ? [
        { label: 'Facturable', value: totals.billableHours },
        { label: 'Non facturable', value: Math.max(0, (totals.allocatedHours || 0) - (totals.billableHours || 0)) },
        { label: 'Disponible', value: Math.max(0, (totals.capacityHours || 0) - (totals.allocatedHours || 0)) },
      ]
    : [];
  const absenceDonut = totals
    ? [
        { label: 'Congés', value: totals.timeoffHours },
        { label: 'Capacité', value: totals.capacityHours },
      ]
    : [];

  const drill = (ctx) =>
    onDrillDown &&
    onDrillDown({
      from: window.from,
      to: window.to,
      period: 'week',
      viewMode: 'scheduled',
      scope,
      ...ctx,
    });

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2 style={sectionTitle}>
        <LayoutDashboard size={16} /> Utilisation &amp; capacité
        <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>
          · 12 prochaines semaines
        </span>
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 16,
        }}
      >
        <DashboardWidget
          title="Capacité de livraison"
          metricKey="deliveryCapacity"
          loading={loading}
          onDrillDown={() => drill({})}
          footer={
            totals
              ? `Capacité ${totals.capacityHours}h · Demande ${totals.allocatedHours}h · Utilisation ${totals.utilizationPct ?? '—'}%`
              : null
          }
        >
          <LineAreaChart data={series} height={260} formatLabel={fmtBucketLabel} />
        </DashboardWidget>

        <DashboardWidget
          title="Utilisation"
          metricKey="utilization"
          loading={loading}
          onDrillDown={() => drill({})}
        >
          <Donut data={utilizationDonut} centerLabel={totals ? `${totals.utilizationPct ?? 0}%` : '—'} />
        </DashboardWidget>

        <DashboardWidget
          title="Absences"
          metricKey="deliveryCapacity"
          loading={loading}
          onDrillDown={() => drill({})}
        >
          <Donut data={absenceDonut} />
        </DashboardWidget>

        <DashboardWidget
          title="Capacité vs demande"
          metricKey="deliveryCapacity"
          loading={loading}
          onDrillDown={() => drill({})}
        >
          <BarChart data={series} height={260} formatLabel={fmtBucketLabel} />
        </DashboardWidget>
      </div>
    </section>
  );
}

export default function DashboardView({ user, onDrillDown, onOpenDetail, onNavigate }) {
  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';
  const canSeeTeam = isAdmin || isManager;

  // Scope demandé pour la partie agrégée. ADMIN peut basculer team/all.
  const [scope, setScope] = useState(canSeeTeam ? 'team' : 'self');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Sprint 23 — alertes budget (calculées à la volée, scopées serveur).
  const [budgetAlerts, setBudgetAlerts] = useState([]);
  // Ticket #24 — vue succincte collaborateur (indicateur seul, aucun montant).
  const [myBudgets, setMyBudgets] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiBudget
      .alerts()
      .then((r) => {
        if (!cancelled) setBudgetAlerts(r.alerts ?? []);
      })
      .catch(() => {});
    apiBudget
      .myAffaires()
      .then((r) => {
        if (!cancelled) setMyBudgets(r.items ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async (s) => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiDashboard.get(s));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(scope);
  }, [load, scope]);

  if (loading && !data) {
    return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Chargement…</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 32 }}>
        <div
          style={{
            padding: '10px 14px',
            background: 'var(--glass)',
            border: '1px solid var(--danger)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--danger)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { config, personal, team, window: win } = data;
  const teamAlertCount = team
    ? team.rows.filter(
        (r) => r.scheduling.alert || r.prevWeek.alert || r.currentWeek.alert,
      ).length
    : 0;

  const scopePill = (key, label, Icon) => {
    const active = scope === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => setScope(key)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: active ? 'var(--accent)' : 'transparent',
          color: active ? '#fff' : 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-pill)',
          padding: '6px 14px',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <Icon size={14} />
        {label}
      </button>
    );
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <LayoutDashboard size={20} /> Accueil
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
          Vue d'ensemble de votre planification et de votre saisie des temps.
          Capacité estimée à 8h par jour ouvré (hors fériés et congés validés).
        </p>
      </div>

      {/* ---- Alertes budget (Sprint 23) ---- */}
      {budgetAlerts.length > 0 ? <BudgetAlertsBanner alerts={budgetAlerts} onNavigate={onNavigate} /> : null}

      {/* ---- Budget de mes affaires (ticket #24 — indicateur seul, aucun montant) ---- */}
      {myBudgets.length > 0 ? (
        <section
          style={{
            padding: '14px 18px',
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)', fontWeight: 600 }}>
            <Wallet size={16} />
            Budget de mes affaires
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {myBudgets.map((b) => {
              const pct = b.consumptionPct != null ? Math.round(b.consumptionPct) : null;
              const dotColor =
                b.status === 'over'
                  ? 'var(--danger)'
                  : b.status === 'warning'
                    ? 'var(--warning)'
                    : 'var(--success)';
              return (
                <div
                  key={b.affaireId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 13,
                    color: 'var(--text)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)',
                    border: b.isAlert ? '1px solid var(--warning)' : '1px solid transparent',
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: dotColor,
                      flexShrink: 0,
                    }}
                    title={
                      b.status === 'over'
                        ? 'Budget dépassé'
                        : b.status === 'warning'
                          ? 'Seuil d’alerte atteint'
                          : 'Budget sous contrôle'
                    }
                  />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.name}
                    {b.code ? <span style={{ color: 'var(--text-muted)' }}> · {b.code}</span> : null}
                  </span>
                  {b.remainingDays != null ? (
                    <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {b.remainingDays.toFixed(1)} j restants
                    </span>
                  ) : null}
                  <span
                    style={{
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 500,
                      background: 'var(--glass)',
                      color: 'var(--text)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pct != null ? `${pct}%` : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* ---- Partie personnelle ---- */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 style={sectionTitle}>
          <UserIcon size={16} /> Ma situation
        </h2>
        {personal ? (
          <MetricsRow
            metrics={personal}
            window={win}
            config={config}
            onOpenDetail={onOpenDetail ? (m) => onOpenDetail(m, 'self') : undefined}
          />
        ) : (
          <div
            style={{
              padding: '16px 18px',
              background: 'var(--card-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-muted)',
              fontSize: 13,
            }}
          >
            Aucune fiche collaborateur n'est liée à votre compte — la situation
            personnelle n'est pas disponible.
          </div>
        )}
      </section>

      {/* ---- Suivi du temps par affaire (ticket #56 — tous rôles) ---- */}
      <AffaireTimeSection user={user} sectionTitle={sectionTitle} />

      {/* ---- Relevé d'heures (ticket #113 — CERFA 2041-AE, tous rôles) ---- */}
      <TimeStatementSection user={user} sectionTitle={sectionTitle} />

      {/* ---- Partie équipe / tout le monde ---- */}
      {canSeeTeam ? (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <h2 style={sectionTitle}>
              <Users size={16} />
              {scope === 'all' ? 'Tout le monde' : 'Mon équipe'}
              {team ? (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>
                  · {team.aggregate.personCount} personne
                  {team.aggregate.personCount > 1 ? 's' : ''}
                </span>
              ) : null}
            </h2>
            {isAdmin ? (
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                {scopePill('team', 'Mon équipe', Users)}
                {scopePill('all', 'Tout le monde', Globe)}
              </div>
            ) : null}
          </div>

          {teamAlertCount > 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 14px',
                background: 'var(--glass)',
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text)',
                fontSize: 13,
              }}
            >
              <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />
              {teamAlertCount} personne{teamAlertCount > 1 ? 's' : ''} en alerte (planification
              ou saisie sous le seuil).
            </div>
          ) : null}

          {team ? (
            <>
              <MetricsRow
                metrics={team.aggregate}
                window={team.window}
                config={config}
                onOpenDetail={onOpenDetail ? (m) => onOpenDetail(m, scope) : undefined}
              />
              <TeamTable rows={team.rows} />
            </>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {loading ? 'Chargement…' : 'Aucune donnée d’équipe.'}
            </div>
          )}
        </section>
      ) : null}

      {/* ---- Utilisation & capacité (widgets multi-charts) ---- */}
      {/* #18 — section analytique (drill-down vers l'onglet Rapports) réservée
          aux ADMIN/MANAGER : un USER/VIEWER n'y a pas accès et ses boutons
          (rapport détaillé) paraissaient inopérants. On masque toute la section
          pour eux ; leurs KPIs personnels restent dans les cartes ci-dessus. */}
      {canSeeTeam ? (
        <UtilizationSection scope={scope} onDrillDown={onDrillDown} />
      ) : null}
    </div>
  );
}
